import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDispatch } from "react-redux";
import { pushPage, pushPageSize } from "store/state/stateSlice";

export const Paginator: React.FC<{
  paginationOption?: {
    page: number;
    pageSize: number;
    totalItems: number;
    numberOfPages: number;
  };
}> = ({ paginationOption }) => {
  const dispatch = useDispatch();
  if (!paginationOption) return null;

  const { page, numberOfPages, totalItems } = paginationOption;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= numberOfPages) {
      dispatch(pushPage(newPage as any));
      dispatch(pushPageSize(paginationOption.pageSize as any));
    }
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(pushPage(1 as any));
    dispatch(pushPageSize(pageSize as any));
  };

  const getPages = () => {
    const pages: (number | string)[] = [];

    if (numberOfPages <= 5) {
      for (let i = 1; i <= numberOfPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      const start = Math.max(2, page - 1);
      const end = Math.min(numberOfPages - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < numberOfPages - 2) pages.push("...");
      pages.push(numberOfPages);
    }

    return pages;
  };

  const pages = getPages();

  return (
    <div className='flex justify-center md:justify-end bg-white sm:mr-4 w-full sm:w-fit sm:rounded-t-lg fixed right-0 p-2 sm:pb-3 bottom-0 mb-5 sm:mb-7 flex-wrap gap-2'>
      <div className='w-18 h-8 flex'>
        <select
          id='pageSelect'
          value={page}
          onChange={(e) => handlePageChange(Number(e.target.value))}
          className='border w-10 sm:w-14 h-6 sm:h-8 px-1 sm:px-2 flex items-center justify-center rounded-md text-p3 sm:text-p2 bg-white text-gray-800'
        >
          {Array.from({ length: numberOfPages }, (_, i) => i + 1).map((p) => (
            <option
              key={p}
              value={p}
            >
              {p}
            </option>
          ))}
        </select>
      </div>
      <div className='md:col-span-12 text-center'>
        <ul className='flex items-center space-x-2'>
          <li
            onClick={() => handlePageChange(page - 1)}
            className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-p3 sm:text-p2 rounded-md p-1 ${
              page === 1
                 ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "cursor-pointer hover:bg-gray-100 text-gray-700"
            }`}
          >
            <ChevronLeft size={16}/>
          </li>

          {pages.map((p, idx) => (
            <li
              key={idx}
              onClick={() => typeof p === "number" && handlePageChange(p)}
              className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-p3 sm:text-p2 rounded-md p-1 ${
                p === "..."
                  ? "cursor-default text-gray-400"
                  : page === p
                  ? "bg-theme700 text-white"
                  : "cursor-pointer text-gray-600 hover:bg-gray-100"
              }`}
            >
              {p}
            </li>
          ))}

          <li
            onClick={() => handlePageChange(page + 1)}
            className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-p3 sm:text-p2 rounded-md p-1 ${
              page === numberOfPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "cursor-pointer hover:bg-gray-100 text-gray-700"
            }`}
          >
            <ChevronRight size={16} />
          </li>
        </ul>
      </div>

      <div className=' w-10 sm:w-14 h-6 sm:h-8 px-1 sm:px-2 flex items-center justify-center text-p3 sm:text-p2 rounded-md bg-gray-100'>
        <select
          onChange={(e) =>
            handlePageSizeChange(parseInt(e.currentTarget.value, 10))
          }
          className='text-gray-900 bg-gray-100 rounded-md flex items-center justify-center text-p2 '
        >
          <option value='10'>10</option>
          {totalItems >= 10 && <option value='20'>20</option>}
          {totalItems >= 20 && <option value='50'>50</option>}
          {totalItems >= 50 && <option value='100'>100</option>}
        </select>
      </div>
    </div>
  );
};
